<script setup>
import { updateFuturesPaymentMethod } from '#common/services/checkoutServices'
import { submitNewPaymentMethod } from '#common/services/paymentServices'
import { scrollToElement } from '#common/utils/form'
import { isApplePaySupported } from '#common/utils/payment'

defineProps({
  basket: {
    type: Object,
    required: false,
    default: () => {
      return {}
    },
  },
  editable: {
    type: Boolean,
    required: false,
    default: false,
  },
  paypalButtonStyle: {
    type: Object,
    required: false,
    default: () => {
      return {
        size: 'large',
        color: 'white',
        shape: 'rect',
        label: 'paypal',
        tagline: 'false',
      }
    },
  },
  applePayButtonStyle: {
    type: Object,
    required: false,
    default: () => {
      return {
        buttonstyle: 'white-outline',
        type: 'plain',
        buttonWidth: '350px',
        buttonHeight: '45px',
      }
    },
  },
  hostedFieldsStyles: {
    type: Object,
    required: false,
    default: () => {
      return {
        'input,select': {
          'font-family': '"Nunito Sans", sans-serif',
          'font-size': '1rem',
          padding: '1.3rem 1rem 0.4rem',
          '-webkit-appearance': 'none',
          outline: 'none',
        },
        '.invalid': {
          color: 'red',
        },
        '.valid': {
          color: 'inherit',
        },
        ':focus': {
          color: 'inherit',
        },
      }
    },
  },
  newCCFormStyles: {
    type: Object,
    required: false,
    default: () => {
      return {}
    },
  },
  shippingAddress: {
    type: Object,
    required: false,
    default: null,
  },
  total: {
    type: Number,
    required: true,
  },
})
const { $auth, $axios } = useNuxtApp()
const emit = defineEmits([
  'cardUpdated',
  'selectedPaymentOptionChanged',
  'selectedPaymentMethodChanged',
])
const loaded = ref(false)
const paymentMethods = usePaymentMethods()
const showNewPayment = ref(false)
const oneTimePaymentMethod = ref(null)
const newCCForm = ref(null)
const submitting = ref(false)
const selectedPaymentOption = ref('')
const savePaymentDetails = ref(true)
const selectedPaymentMethod = ref(null)
const saveError = ref(null)

const loggedIn = computed(() => {
  return $auth.loggedIn
})

const allCreditCardMethods = computed(() => {
  const storeMethods = paymentMethods.creditCardMethods
    ? [...paymentMethods.creditCardMethods]
    : []

  // Check if a one-time credit card exists and is NOT already in the store's list.
  if (
    oneTimePaymentMethod.value &&
    oneTimePaymentMethod.value.type === 'CreditCard' &&
    !storeMethods.some((m) => m.card_id === oneTimePaymentMethod.value.token)
  ) {
    // If it's missing, add it to our temporary list for rendering.
    // This provides a fallback if the store fails to update.
    storeMethods.push({
      card_id: oneTimePaymentMethod.value.token,
      is_default: false,
      is_cc: true,
      is_applepay: false,
      is_paypal: false,
      last_4: oneTimePaymentMethod.value.details.lastFour,
      expiration_date: `${oneTimePaymentMethod.value.details.expirationMonth}/${oneTimePaymentMethod.value.details.expirationYear}`,
      summary_string: `${oneTimePaymentMethod.value.details.cardType} •••• •••• •••• ${oneTimePaymentMethod.value.details.lastFour}`,
      one_time: true,
    })
  }

  return storeMethods
})

const applePaySupported = computed(() => {
  if (import.meta.client) {
    return isApplePaySupported(window)
  }
  return false
})
const showApplePay = computed(() => {
  return applePaySupported.value || paymentMethods.applePayMethods.length > 1
})
const showMakeDefaultCheckbox = computed(() => {
  return paymentMethods.creditCardMethods.length > 1
})
watch(loggedIn, (newVal) => {
  if (newVal) {
    fetchPaymentMethods()
  }
})
watch(selectedPaymentOption, (newVal) => {
  if (newVal) {
    emit('selectedPaymentOptionChanged', newVal)
  }
})
watch(selectedPaymentMethod, (newVal) => {
  if (newVal) {
    let tokenType = 0
    let selectedMethodObject = null
    if (
      oneTimePaymentMethod.value &&
      oneTimePaymentMethod.value.token === newVal
    ) {
      tokenType = 1
      selectedMethodObject = {
        card_id: oneTimePaymentMethod.value.token,
        summary_string: `${oneTimePaymentMethod.value.details.cardType} •••• •••• •••• ${oneTimePaymentMethod.value.details.lastFour}`,
        expiration_date: `${oneTimePaymentMethod.value.details.expirationMonth}/${oneTimePaymentMethod.value.details.expirationYear}`,
        one_time: true,
      }
    } else {
      // Find in the store for saved methods (PayPal, saved CCs, etc.)
      selectedMethodObject = paymentMethods.userPaymentMethods.find(
        (m) => m.card_id === newVal,
      )
    }

    updateFuturesPaymentMethod($axios, newVal, tokenType)

    // Emit the full object now
    emit('selectedPaymentMethodChanged', selectedMethodObject)
  }
})
onMounted(() => {
  if (!loggedIn.value) {
    showNewPayment.value = true
  } else {
    fetchPaymentMethods()
  }
})
async function fetchPaymentMethods() {
  await paymentMethods.loadPaymentMethods(true)
  selectedPaymentOption.value = paymentMethods.defaultPaymentOption
  selectedPaymentMethod.value = paymentMethods.defaultPaymentMethod
  loaded.value = true
}
function tabChange(newPaymentOption) {
  // Deselect payment method on tab change
  selectedPaymentMethod.value = null
  emit('selectedPaymentMethodChanged', null)
}
function paymentMethodIsSelected(paymentMethod) {
  if (selectedPaymentMethod.value) {
    return selectedPaymentMethod.value === paymentMethod.card_id
  } else {
    return false
  }
}
function addNewCreditCardClicked() {
  selectedPaymentMethod.value = null
  showNewPayment.value = true
}
async function braintreeSubmit(payload, newSelectedPaymentOption, makeDefault) {
  saveError.value = null
  try {
    const resp = await submitNewPaymentMethod($axios, {
      token: payload.nonce,
      make_default: makeDefault,
    })
    // Update list of payment accounts
    await fetchPaymentMethods()
    selectedPaymentOption.value = newSelectedPaymentOption
    selectedPaymentMethod.value = resp.data.payment_token
  } catch (e) {
    if (e.response && e.response.data) {
      saveError.value = e.response.data.display_error
    }
    Sentry.captureException(e)
    console.error('Error submitting braintree payment method', e.response)
  }
}
async function payPalSubmit(payload) {
  if (!loggedIn.value) {
    selectedPaymentOption.value = 'paymentOptionPayPal'
    selectedPaymentMethod.value = payload.nonce
    oneTimePaymentMethod.value = payload
    return
  }
  return await braintreeSubmit(payload, 'paymentOptionPayPal', true)
}
async function applePaySubmit(payload) {
  if (!loggedIn.value) {
    selectedPaymentOption.value = 'paymentOptionApplePay'
    selectedPaymentMethod.value = payload.nonce
    oneTimePaymentMethod.value = payload
    return
  }
  return await braintreeSubmit(payload, 'paymentOptionApplePay', false)
}

async function submitNewCCForm() {
  saveError.value = null

  const tokenizedData = await newCCForm.value.validateAndGetToken()

  if (!tokenizedData) {
    return false
  }

  if (!savePaymentDetails.value) {
    selectedPaymentOption.value = 'paymentOptionCreditCard'
    oneTimePaymentMethod.value = tokenizedData
    selectedPaymentMethod.value = tokenizedData.token
    paymentMethods.addPaymentMethod({
      card_id: tokenizedData.token,
      is_default: false,
      is_cc: true,
      is_applepay: false,
      is_paypal: false,
      last_4: tokenizedData.details.lastFour,
      expiration_date: `${tokenizedData.details.expirationMonth}/${tokenizedData.details.expirationYear}`,
      summary_string: `${tokenizedData.details.cardType} •••• •••• •••• ${tokenizedData.details.lastFour}`,
      one_time: true,
    })
    showNewPayment.value = false
    newCCForm.value.clear()
    return true
  }

  try {
    const resp = await submitNewPaymentMethod($axios, tokenizedData)

    const newMethod = {
      card_id: resp.data.payment_token,
      is_default: tokenizedData.make_default,
      is_cc: true,
      is_applepay: false,
      is_paypal: false,
      last_4: tokenizedData.details.lastFour,
      expiration_date: `${tokenizedData.details.expirationMonth}/${tokenizedData.details.expirationYear}`,
      summary_string: `${tokenizedData.details.cardType} •••• •••• •••• ${tokenizedData.details.lastFour}`,
      one_time: false,
    }

    paymentMethods.addPaymentMethod(newMethod)

    selectedPaymentOption.value = 'paymentOptionCreditCard'
    selectedPaymentMethod.value = newMethod.card_id

    showNewPayment.value = false
    newCCForm.value.clear()
    return true
  } catch (e) {
    Sentry.captureException(e)
    console.error('Failed adding card', e)
    if (e.response && e.response.data) {
      saveError.value = e.response.data.display_error
    } else {
      Sentry.captureException(e)
      console.error('Error submitting braintree payment method', e)
    }
    return false
  }
}
function savePaymentDetailsClicked(value) {
  savePaymentDetails.value = value
}
function ccPaymentSelected(method) {
  selectedPaymentMethod.value = method.card_id
  showNewPayment.value = false
}
function ccSummaryEditClicked(method) {
  selectedPaymentMethod.value = method.card_id
  showNewPayment.value = false
}
function cancelClicked() {
  showNewPayment.value = false
}
function truncatePayPalMethod(email) {
  if (email.length > 28) {
    return email.slice(0, 25) + '...'
  }
  return email
}
function scrollToCardById(cardID) {
  cancelClicked()
  scrollToElement(nextTick, `#${cardID}`)
}
</script>

<template>
  <div class="payment-tabs">
    <div class="tab-panels">
      <div class="tabset">
        <!-- Tab 1 -->
        <input
          id="credit-card"
          v-model="selectedPaymentOption"
          type="radio"
          name="paymentRadio"
          value="paymentOptionCreditCard"
          @click="tabChange('paymentOptionCreditCard')"
        />
        <label for="credit-card" class="credit-card">
          Credit Card
          <div class="image">
            <img src="~/assets/images/svg/logo-visa.svg" alt="Visa Logo" />
            <img
              src="~/assets/images/svg/logo-mastercard.svg"
              alt="Mastercard Logo"
            />
            <img
              src="~/assets/images/svg/logo-amex.svg"
              alt="American Express Logo"
            />
            <img
              src="~assets/images/svg/logo-discover.svg"
              role="img"
              alt="Discover Logo"
            />
          </div>
        </label>
        <!-- Tab 2 -->
        <input
          id="paypal-radio"
          v-model="selectedPaymentOption"
          type="radio"
          name="paymentRadio"
          value="paymentOptionPayPal"
          @click="tabChange('paymentOptionPayPal')"
        />
        <label for="paypal-radio">
          <span class="image" style="margin-left: 0px !important">
            <img src="~/assets/images/svg/logo-paypal.svg" alt="PayPal Logo" />
          </span>
          PayPal
        </label>
        <!-- Tab 3 -->
        <input
          v-if="showApplePay"
          id="applepay"
          v-model="selectedPaymentOption"
          type="radio"
          name="paymentRadio"
          value="paymentOptionApplePay"
          @click="tabChange('paymentOptionApplePay')"
        />
        <label v-if="showApplePay" for="applepay">
          <span class="image" style="margin-left: 0px !important">
            <img src="~/assets/images/svg/logo-apple.svg" alt="ApplePay Logo" />
          </span>
          Pay
        </label>
        <!-- Tab Panels -->
        <div class="tab-panels">
          <section class="tab-panel">
            <div
              v-if="
                loggedIn && selectedPaymentOption === 'paymentOptionCreditCard'
              "
              class="radio-grid stacked small payment-suboptions floating editable-summary"
              style="margin-top: 0 !important"
            >
              <ul id="cc-method-list">
                <template
                  v-for="method in allCreditCardMethods"
                  :key="method.card_id"
                >
                  <CheckoutCreditCardSummaryFK
                    :id="method.card_id"
                    :ref="'cc-summary-' + method.card_id"
                    :payment-method="method"
                    name="paymentMethodOption"
                    :checked="paymentMethodIsSelected(method)"
                    :show-make-default-checkbox="showMakeDefaultCheckbox"
                    :force-default="!showMakeDefaultCheckbox"
                    :editable="editable && !method.one_time"
                    :card="method"
                    :custom-theme="newCCFormStyles"
                    :hosted-fields-styles="hostedFieldsStyles"
                    address-line-one-id="edit-payment-address-line-1"
                    @paymentSelected="ccPaymentSelected(method)"
                    @editClicked="ccSummaryEditClicked(method)"
                    @cardUpdated="$emit('cardUpdated')"
                    @cardEditClosed="scrollToCardById"
                  >
                    <template #nameForm="nameSlotProps">
                      <FloatingNameFormFK
                        id-prefix="edit"
                        :validation-rules="nameSlotProps.validationRules"
                      />
                    </template>
                    <template #hostedRowForm="hostedSlotProps">
                      <FloatingHostedFieldsForm
                        :tokenize-errors="hostedSlotProps.tokenizeErrors"
                        :initial="method"
                      />
                    </template>
                    <template #addressForm="addressSlotProps">
                      <FloatingAddressFormFK
                        id-prefix="edit"
                        :state-options="addressSlotProps.stateOptions"
                        @addressAutocomplete="
                          addressSlotProps.autoCompletePlaceChanged
                        "
                      />
                    </template>
                  </CheckoutCreditCardSummaryFK>
                </template>
                <li v-if="!showNewPayment">
                  <button
                    class="add-new-cc left"
                    @click="addNewCreditCardClicked"
                  >
                    Add New Credit Card
                  </button>
                </li>
              </ul>
            </div>
            <div class="floating new-form">
              <CreditCardFormFK
                v-if="showNewPayment"
                ref="newCCForm"
                include-number
                submit-button-class="primary left"
                :cancel-as-link="true"
                :save-error="saveError"
                :custom-theme="newCCFormStyles"
                :hide-buttons="true"
                :disabled="submitting"
                :show-make-default-checkbox="loggedIn"
                :show-save-payment-details-checkbox="loggedIn"
                :show-pay-pal-button="false"
                :hosted-fields-styles="hostedFieldsStyles"
                :show-billing-same-as-shipping-checkbox="true"
                :shipping-address="shippingAddress"
                address-line-one-id="input-payment-address-line-1"
                @savePaymentDetailsClicked="savePaymentDetailsClicked"
              >
                <template #nameForm="nameSlotProps">
                  <FloatingNameFormFK
                    id-prefix="input"
                    :validation-rules="nameSlotProps.validationRules"
                  />
                </template>
                <template #hostedRowForm="hostedSlotProps">
                  <FloatingHostedFieldsForm
                    :tokenize-errors="hostedSlotProps.tokenizeErrors"
                    :include-number="hostedSlotProps.includeNumber"
                  />
                </template>
                <template #addressForm="addressSlotProps">
                  <FloatingAddressFormFK
                    id-prefix="input"
                    :state-options="addressSlotProps.stateOptions"
                    @addressAutocomplete="
                      addressSlotProps.autoCompletePlaceChanged
                    "
                  />
                </template>
                <template #after-form>
                  <div
                    class="standard-form form-container"
                    style="margin-left: auto"
                  >
                    <CheckoutSectionSave
                      :save-disabled="false"
                      :show-cancel-button="loggedIn"
                      save-button-text="Save"
                      submit-button-class=""
                      :cancel-as-link="true"
                      template-class="future-payment-save"
                      @saveClicked="submitNewCCForm"
                      @cancelClicked="cancelClicked"
                    />
                  </div>
                </template>
              </CreditCardFormFK>
            </div>
          </section>
          <section class="tab-panel" style="margin-bottom: 25px">
            <div
              v-show="selectedPaymentOption === 'paymentOptionPayPal'"
              class="radio-grid stacked small payment-suboptions editable-summary"
              style="margin-top: 0 !important"
            >
              <ul>
                <li
                  v-for="method in paymentMethods.paypalMethods"
                  v-show="loggedIn"
                  :key="method.card_id"
                >
                  <input
                    :id="method.card_id"
                    v-model="selectedPaymentMethod"
                    type="radio"
                    name="paymentMethodOption"
                    :value="method.card_id"
                  />
                  <label :for="method.card_id">
                    <strong :title="method.email">{{
                      truncatePayPalMethod(method.email)
                    }}</strong>
                  </label>
                </li>
                <li>
                  <div>
                    <button
                      aria-label="Pay Pal"
                      style="
                        border: none;
                        background-color: unset;
                        width: unset;
                        margin: 0;
                        position: relative;
                        right: unset;
                      "
                    >
                      <PayPal
                        :amount="total || 0.01"
                        :show-email="!loggedIn"
                        :button-style="paypalButtonStyle"
                        :save-payment-details="loggedIn"
                        :increase-pay-pal-size="true"
                        @tokenized="payPalSubmit"
                      />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </section>
          <section
            v-if="showApplePay"
            class="tab-panel"
            style="margin-bottom: 25px"
          >
            <div
              class="radio-grid stacked small payment-suboptions editable-summary"
              style="margin-top: 0 !important"
            >
              <ul>
                <li
                  v-for="method in paymentMethods.applePayMethods"
                  v-show="loggedIn"
                  :key="method.card_id"
                >
                  <input
                    :id="method.card_id"
                    v-model="selectedPaymentMethod"
                    type="radio"
                    name="paymentMethodOption"
                    :value="method.card_id"
                  />
                  <label :for="method.card_id">
                    <strong>
                      {{ method.summary_string }}
                      <span v-if="method.expiration_date" class="cc-expiration">
                        <br />
                        expires {{ method.expiration_date }}
                      </span>
                    </strong>
                  </label>
                </li>
                <li v-if="applePaySupported">
                  <button
                    aria-label="Apple Pay"
                    class="add-new-cc apple-pay-button"
                    style="border: none; background-color: unset; width: unset"
                  >
                    <ApplePay
                      style="padding-bottom: 25px"
                      :amount="total"
                      :button-style="applePayButtonStyle"
                      @tokenized="applePaySubmit"
                    />
                  </button>
                  <div
                    v-if="
                      oneTimePaymentMethod &&
                      oneTimePaymentMethod.type === 'ApplePayCard'
                    "
                  >
                    Apple Pay -
                    {{ oneTimePaymentMethod.paymentMethod.displayName }}
                  </div>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
